import axios from 'axios';
import { isWeb } from '../../../helpers/platformHelpers';
import { injectedStore } from '../../ConfigureStore';
import { injectedFetchBlob } from '../../ConfigureGlobals';

// Axios-style join: apiBaseURL ends with '/' on mobile but not on web, and
// callers pass urls with and without a leading slash — normalize both.
const joinURL = (baseURL: string, url: string): string =>
  `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;

const mimeFromExtension = (uri: string): string | undefined => {
  const match = uri.toLowerCase().match(/\.([a-z0-9]+)(?:\?.*)?$/);
  const ext = match?.[1];
  switch (ext) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'pdf':
      return 'application/pdf';
    default:
      return undefined;
  }
};

export const uploadFileFromStorage = async (
  url: string,
  fileUri: string,
  method: 'POST' | 'PUT' = 'POST',
  contentType?: string
): Promise<any> => {
  const state = injectedStore.getState();
  const { apiBaseURL, apiVersion, xBrandId } = state.config;
  const { deviceUUID, platform, model, pushToken, pushDeviceID, userAgent } =
    state.device;

  const headers = {
    Authorization: 'Bearer access-token...',
    'Content-Type': 'application/octet-stream',
    Accept: 'application/json; charset=utf-8',
    'X-API-Version': apiVersion,
    'X-Application-Client': 'mobile',
    'Cache-Control': 'no-cache',
  };

  if (xBrandId) {
    // @ts-ignore
    headers['X-Brand-ID'] = xBrandId;
  }

  if (deviceUUID) {
    // @ts-ignore
    headers['X-Device-UUID'] = deviceUUID;
  }

  if (platform) {
    // @ts-ignore
    headers['X-Device-Platform'] = `${platform.name} ${platform.version}`;
  }

  if (model) {
    // @ts-ignore
    headers['X-Device-Model'] = model;
  }

  if (pushToken) {
    // @ts-ignore
    headers['X-Push-Token'] = pushToken;
  }

  if (pushDeviceID) {
    // @ts-ignore
    headers['X-Push-Device-ID'] = pushDeviceID;
  }

  if (userAgent) {
    // @ts-ignore
    headers['User-Agent'] = `${userAgent} React-Native`;
  }

  if (state.userData.user.basicAuth) {
    headers.Authorization = state.userData.user.basicAuth;
  }

  if (!fileUri) {
    return { error: 'No fileUri provided' };
  }

  if (isWeb()) {
    try {
      let data: Blob | null = null;
      if (fileUri.startsWith('blob:')) {
        const response = await fetch(fileUri);
        data = await response.blob();
      }

      const resolvedContentType =
        contentType || data?.type || 'application/octet-stream';
      headers['Content-Type'] = resolvedContentType;

      const axiosResponse = await axios({
        method: method,
        url: joinURL(apiBaseURL, url),
        headers: headers,
        data: data,
      });
      URL.revokeObjectURL(fileUri);
      return { data: axiosResponse.data };
    } catch (error) {
      return { error: error.message };
    }
  } else {
    let wrappedPath: string;
    if (fileUri.startsWith('file://')) {
      wrappedPath = 'RNFetchBlob-' + fileUri;
    } else {
      wrappedPath = injectedFetchBlob.wrap(fileUri);
    }
    headers['Content-Type'] =
      contentType || mimeFromExtension(fileUri) || 'application/octet-stream';
    try {
      const response = await injectedFetchBlob.fetch(
        method,
        joinURL(apiBaseURL, url),
        headers,
        wrappedPath
      );

      if (response.respInfo.status.toString().startsWith('2')) {
        return { data: response.data };
      }
      return { error: response.respInfo.status };
    } catch (error) {
      return { error: error.message };
    }
  }
};

// Upload a file from local storage to an absolute URL (e.g. a presigned S3
// URL). No auth/brand headers: S3 rejects presigned-URL requests that also
// carry an Authorization header, and the Content-Type must match what the
// backend signed ('image/jpeg', same as web's makeS3UploadAPICall).
export const uploadFileFromStorageToUrl = async (
  absoluteUrl: string,
  fileUri: string,
  method: 'POST' | 'PUT' = 'PUT',
  contentType: string = 'image/jpeg'
): Promise<any> => {
  if (!fileUri) {
    return { error: 'No fileUri provided' };
  }

  const headers = {
    'Content-Type': contentType,
  };

  if (isWeb()) {
    try {
      let data: Blob | null = null;
      if (fileUri.startsWith('blob:')) {
        const response = await fetch(fileUri);
        data = await response.blob();
      }

      const axiosResponse = await axios({
        method: method,
        url: absoluteUrl,
        headers: headers,
        data: data,
      });
      return { data: axiosResponse.data };
    } catch (error) {
      return { error: error.message };
    }
  } else {
    let wrappedPath: string;
    if (fileUri.startsWith('file://')) {
      wrappedPath = 'RNFetchBlob-' + fileUri;
    } else {
      wrappedPath = injectedFetchBlob.wrap(fileUri);
    }
    try {
      const response = await injectedFetchBlob.fetch(
        method,
        absoluteUrl,
        headers,
        wrappedPath
      );

      if (response.respInfo.status.toString().startsWith('2')) {
        return { data: response.data };
      }
      return { error: response.respInfo.status };
    } catch (error) {
      return { error: error.message };
    }
  }
};
